import html2canvas from 'html2canvas';

/* =============================================================
   Taking the screenshot, drawing on it, and turning it into a file.

   Capture prefers the browser's own screen capture (getDisplayMedia):
   it is the real pixels, so every CSS feature the app uses comes out
   right, and it costs one "share this tab" confirmation. html2canvas
   redraws the page itself instead and does not understand some modern
   CSS (color-mix, used across the app), so it is only the fallback for
   a browser without screen capture -- phones, mostly.
   ============================================================= */

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

const captureWithDisplayMedia = async () => {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: false,
    // Chrome: offer this tab first, and do not let the share switch away.
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
  });

  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();
    // Let the browser's share prompt leave the page before the frame is taken.
    await wait(350);
    await nextFrame();

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
};

const captureWithHtml2canvas = () =>
  html2canvas(document.body, {
    useCORS: true,
    logging: false,
    x: window.scrollX,
    y: window.scrollY,
    width: window.innerWidth,
    height: window.innerHeight,
    scale: Math.min(window.devicePixelRatio || 1, 2),
  });

/**
 * A canvas of what is on screen. Rejects with a NotAllowedError when the
 * person dismisses the share prompt -- that is a "no", not a failure, so it
 * does not fall back to html2canvas.
 */
export const captureScreen = async () => {
  if (navigator.mediaDevices?.getDisplayMedia) {
    try {
      return await captureWithDisplayMedia();
    } catch (error) {
      if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') throw error;
      // Anything else (an option the browser rejects, no video frame):
      // try the other way rather than give up.
    }
  }
  return captureWithHtml2canvas();
};

/** One marker stroke: { color, alpha, width, points: [{x, y}] } in image pixels. */
export const drawStroke = (ctx, stroke) => {
  const points = stroke?.points || [];
  if (!points.length) return;
  ctx.save();
  ctx.globalAlpha = stroke.alpha ?? 1;
  ctx.strokeStyle = stroke.color;
  ctx.lineWidth = stroke.width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  // A click with no movement still leaves a dot.
  if (points.length === 1) ctx.lineTo(points[0].x + 0.01, points[0].y);
  for (let i = 1; i < points.length; i += 1) ctx.lineTo(points[i].x, points[i].y);
  ctx.stroke();
  ctx.restore();
};

const CAPTION_FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';

/** Word-wrap `text` to `maxWidth`, breaking a word only when it is wider than a line.
 *  Every line is kept: the band under the image grows to fit the whole note
 *  (composeImage sizes it from the line count). It used to stop at twelve
 *  lines and end in "…", so a long note lost its end. The note is capped at
 *  2,000 characters where it is typed, which keeps the image a sane size. */
const wrapLines = (ctx, text, maxWidth) => {
  const lines = [];
  String(text)
    .split('\n')
    .forEach((paragraph) => {
      let line = '';
      paragraph.split(/\s+/).filter(Boolean).forEach((word) => {
        let piece = word;
        while (ctx.measureText(piece).width > maxWidth && piece.length > 1) {
          let cut = piece.length - 1;
          while (cut > 1 && ctx.measureText(piece.slice(0, cut)).width > maxWidth) cut -= 1;
          if (line) {
            lines.push(line);
            line = '';
          }
          lines.push(piece.slice(0, cut));
          piece = piece.slice(cut);
        }
        const candidate = line ? `${line} ${piece}` : piece;
        if (line && ctx.measureText(candidate).width > maxWidth) {
          lines.push(line);
          line = piece;
        } else {
          line = candidate;
        }
      });
      lines.push(line);
    });

  return lines;
};

/**
 * The finished image: the screenshot, the marker strokes, and a white band
 * underneath carrying the note and, after it, who took the screenshot --
 * so a pasted image says what it is about and who sent it wherever it goes.
 */
export const composeImage = (shot, strokes, comment = '', byline = '') => {
  const text = String(comment || '').trim();
  const by = String(byline || '').trim();
  const fontSize = Math.max(16, Math.round(shot.width / 64));
  const pad = Math.round(fontSize * 1.1);
  const lineHeight = Math.round(fontSize * 1.4);
  const font = `${fontSize}px ${CAPTION_FONT}`;
  // The byline is smaller and grey: it is attribution, not the message.
  const byFontSize = Math.max(13, Math.round(fontSize * 0.8));
  const byLineHeight = Math.round(byFontSize * 1.4);
  const byFont = `${byFontSize}px ${CAPTION_FONT}`;

  const measure = document.createElement('canvas').getContext('2d');
  measure.font = font;
  const lines = text ? wrapLines(measure, text, shot.width - pad * 2) : [];
  measure.font = byFont;
  const byLines = by ? wrapLines(measure, by, shot.width - pad * 2).slice(0, 2) : [];
  const gap = lines.length && byLines.length ? Math.round(fontSize * 0.5) : 0;
  const textHeight = lines.length * lineHeight + gap + byLines.length * byLineHeight;
  const band = textHeight ? pad * 2 + textHeight : 0;

  const out = document.createElement('canvas');
  out.width = shot.width;
  out.height = shot.height + band;
  const ctx = out.getContext('2d');
  ctx.drawImage(shot, 0, 0);
  (strokes || []).forEach((stroke) => drawStroke(ctx, stroke));

  if (band) {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, shot.height, out.width, band);
    ctx.fillStyle = '#D0D5DD';
    ctx.fillRect(0, shot.height, out.width, Math.max(1, Math.round(fontSize / 16)));
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#101828';
    ctx.font = font;
    lines.forEach((line, i) => {
      ctx.fillText(line, pad, shot.height + pad + i * lineHeight + (lineHeight - fontSize) / 2);
    });

    const byTop = shot.height + pad + lines.length * lineHeight + gap;
    ctx.fillStyle = '#5A6A80';
    ctx.font = byFont;
    byLines.forEach((line, i) => {
      ctx.fillText(line, pad, byTop + i * byLineHeight + (byLineHeight - byFontSize) / 2);
    });
  }
  return out;
};

export const canvasToBlob = (canvas, type = 'image/png', quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image'))), type, quality);
  });
